import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface RateLimitStats {
  anonymous: {
    current_usage: number;
    limit: number;
    remaining: number;
    percentage_used: number;
  };
  authenticated: {
    current_usage: number;
    limit: number;
    remaining: number;
    percentage_used: number;
  };
}

interface RateLimitConfig {
  anonymous_limit: number;
  authenticated_limit: number;
  admin_limit: string;
  window_hours: number;
}

interface RateLimitResponse {
  rate_limit_stats: RateLimitStats;
  config: RateLimitConfig;
  description: {
    anonymous: string;
    authenticated: string;
    admin: string;
  };
}

const RateLimitStats = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState<RateLimitResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/rate-limits', {
        headers: {
          'Authorization': `Bearer ${document.cookie.split('auth_token=')[1]?.split(';')[0]}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch rate limit stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      // Refresh stats every 30 seconds
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limiting Statistics</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
          <span className="ml-3 text-gray-600">Loading stats...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Rate Limiting Statistics</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800 text-sm">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Rate Limiting Statistics</h3>
        <button
          onClick={fetchStats}
          className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Anonymous Users */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Anonymous Users</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUsageColor(stats.rate_limit_stats.anonymous.percentage_used)}`}>
              {Math.round(stats.rate_limit_stats.anonymous.percentage_used)}% used
            </span>
          </div>
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Current usage:</span>
              <span className="font-medium">{stats.rate_limit_stats.anonymous.current_usage}</span>
            </div>
            <div className="flex justify-between">
              <span>Limit:</span>
              <span className="font-medium">{stats.rate_limit_stats.anonymous.limit} per hour</span>
            </div>
            <div className="flex justify-between">
              <span>Remaining:</span>
              <span className="font-medium">{stats.rate_limit_stats.anonymous.remaining}</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(stats.rate_limit_stats.anonymous.percentage_used)}`}
                style={{ width: `${Math.min(stats.rate_limit_stats.anonymous.percentage_used, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Authenticated Users */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">Authenticated Users</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUsageColor(stats.rate_limit_stats.authenticated.percentage_used)}`}>
              {Math.round(stats.rate_limit_stats.authenticated.percentage_used)}% used
            </span>
          </div>
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span>Current usage:</span>
              <span className="font-medium">{stats.rate_limit_stats.authenticated.current_usage}</span>
            </div>
            <div className="flex justify-between">
              <span>Limit:</span>
              <span className="font-medium">{stats.rate_limit_stats.authenticated.limit} per hour</span>
            </div>
            <div className="flex justify-between">
              <span>Remaining:</span>
              <span className="font-medium">{stats.rate_limit_stats.authenticated.remaining}</span>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(stats.rate_limit_stats.authenticated.percentage_used)}`}
                style={{ width: `${Math.min(stats.rate_limit_stats.authenticated.percentage_used, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Configuration</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Anonymous:</span>
            <span className="ml-2 font-medium">{stats.config.anonymous_limit}/hour</span>
            <p className="text-xs text-gray-500 mt-1">{stats.description.anonymous}</p>
          </div>
          <div>
            <span className="text-gray-600">Authenticated:</span>
            <span className="ml-2 font-medium">{stats.config.authenticated_limit}/hour</span>
            <p className="text-xs text-gray-500 mt-1">{stats.description.authenticated}</p>
          </div>
          <div>
            <span className="text-gray-600">Admin:</span>
            <span className="ml-2 font-medium">{stats.config.admin_limit}</span>
            <p className="text-xs text-gray-500 mt-1">{stats.description.admin}</p>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        <p>💡 These are global limits shared across all users in each category.</p>
        <p>🔄 Statistics refresh automatically every 30 seconds.</p>
      </div>
    </div>
  );
};

export default RateLimitStats; 